/**
 * Monitor app bootstrap.
 *
 * System API WebSocket is mandatory and lives in `system-api-ws.bootstrap`.
 * This store controls only the optional monitor producer connection, which can
 * be started manually or by a remote command received through System API WS.
 */

import { monitorService, rtLogService } from '@rammetec/core'
import { integrationFactory, repositoryFactory } from '@rammetec/data-access'
import { globalConfigService } from '@syscolect/application'
import { env } from '@/config/env'
import { authService } from './authService'
import { ensureConsoleSession, getConsoleSessionInfo } from './console-session.bootstrap'

export type MonitoringStatus = 'idle' | 'connecting' | 'connected' | 'error'

let status: MonitoringStatus = 'idle'
let lastError: string | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function setStatus(next: MonitoringStatus, error: string | null = null): void {
  status = next
  lastError = error
  emit()
}

/** Initializes session + monitor services and opens the monitor producer WS. */
async function openConnection(): Promise<boolean> {
  const isDev = env.appEnvironment === 'development'

  await ensureConsoleSession()

  // GlobalConfig is already loaded by <ConfigGate> at app boot — consume only.
  const globalConfig = globalConfigService.getConfig()

  const monitorHost = env.monitorWsHost ?? globalConfig?.monitorHost

  if (!monitorHost) {
    rtLogService.warn('[monitoring.bootstrap] monitor host not configured — skipping connect')
    return false
  }

  const { sessionId, sessionHash } = getConsoleSessionInfo()
  monitorService.init({
    monitorHost,
    sessionId,
    sessionHash,
    isDev,
    platform: 'web',
    getToken: async () => (await authService.getIdToken()) ?? '',
  })

  repositoryFactory.attachMonitorSubscriptions()
  integrationFactory.attachMonitorSubscriptions()

  return true
}

export const monitoringStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getStatus(): MonitoringStatus {
    return status
  },
  getError(): string | null {
    return lastError
  },
  /** Opens the optional monitor producer connection (idempotent). */
  async connect(): Promise<void> {
    if (status === 'connected' || status === 'connecting') return
    setStatus('connecting')
    try {
      const connected = await openConnection()
      if (connected) {
        setStatus('connected')
      } else {
        setStatus('error', 'Host de monitoramento não configurado.')
      }
    } catch (error) {
      rtLogService.error('[monitoring.bootstrap] connect failed', { error })
      setStatus('error', error instanceof Error ? error.message : 'Falha ao conectar.')
    }
  },
  /** Marks monitor as idle. Core monitorService does not expose a transport close yet. */
  disconnect(): void {
    setStatus('idle')
  },
}

// Dev convenience: auto-connect only when explicitly opted in via env.
if (env.monitorEnabled) {
  void monitoringStore.connect()
}
